import { Icon, ToolTipWrapper, TOP_TIP_POSITION } from '@src/quince-components/src'
import ProductComparisonBrand from '@src/quince-site-common/src/domains/ProductComparisonBrand'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import IButton from '@src/quince-site-common/src/icons/iButtton.inline.svg'
import {
  MF_PC_ADDITONAL_ATTRIBUTE_NAME,
  PPS_PC_ADDITONAL_ATTRIBUTE_NAME,
  SERVING_PC_ADDITONAL_ATTRIBUTE_NAME,
  WGD_PC_ADDITONAL_ATTRIBUTE_NAME,
} from '@src/quince-site-common/src/utils/constants'
import { formatPrice } from '@src/quince-site-common/src/utils/formatPrice'
import { useTranslation } from 'next-i18next'
import React from 'react'

import AdditionalPropertyComponent from './AdditionalPropertyComponent'
import ComparisonRow from './ComparisonRow'
import CrossIcon from './icons/cross.inline.svg'
import GrayCircle from './icons/gray-circle.inline.svg'
import YellowCircle from './icons/yellow-circle.inline.svg'
import { getBrandsExtraPrice, getTooltipContent, getVitaminSavingsPercentage } from './productPriceComparision.helper'
import styles from './productPriceComparision.module.scss'

interface ProductPriceComparisionProps {
  quincePrice: number
  productComparisionsBrands?: ProductComparisonBrand[]
  hideFreeReturns?: boolean
  showDollarComparisonPrice?: boolean
  hasRestockingFee?: boolean
  showGiftCardCredit?: boolean
  giftCardInformation?: Partial<{
    giftCardCredit: number
    savings: string
    productComparisionsBrands: ProductComparisonBrand[]
  }>
  isVitamin?: boolean
  servings?: number
  pricePerServing?: number
}

export const YellowCircleContainer = () => {
  const { t } = useTranslation('pdp')
  return (
    <div className={styles.productPriceComparision__bodyRow__cell__yellowCircleWrapper}>
      <span className="sr-only">{t('yes')}</span>
      <Icon iconSvg={YellowCircle} className={styles.circleIcon} />
    </div>
  )
}

export const GrayCircleContainer = () => {
  const { t } = useTranslation('pdp')
  return (
    <div className={styles.productPriceComparision__bodyRow__cell__grayCircleWrapper}>
      <span className="sr-only">{t('yes')}</span>
      <Icon iconSvg={GrayCircle} className={styles.circleIcon} />
    </div>
  )
}

export const CrossIconContainer = () => {
  const { t } = useTranslation('pdp')
  return (
    <div className={styles.productPriceComparision__bodyRow__cell__crossIconWrapper}>
      <span className="sr-only">{t('no')}</span>
      <Icon iconSvg={CrossIcon} className={styles.crossIcon} />
    </div>
  )
}

const IButtonIconContainer = () => (
  <span className={styles.productPriceComparision__bodyRow__cell__iButtonIconWrapper}>
    <Icon iconSvg={IButton} className={styles.iButtonIcon} />
  </span>
)

const ProductPriceComparision = ({
  quincePrice,
  productComparisionsBrands,
  hideFreeReturns = false,
  showDollarComparisonPrice,
  hasRestockingFee,
  showGiftCardCredit,
  giftCardInformation,
  servings,
  pricePerServing,
  isVitamin,
}: ProductPriceComparisionProps) => {
  const { deviceType } = useDeviceInfo()
  const { t } = useTranslation('pdp')
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  const tooltip = getTooltipContent(productComparisionsBrands ?? [])

  productComparisionsBrands?.sort(
    (brandOne, brandTwo) => (brandOne.prices[0]?.price || 0) - (brandTwo.prices[0]?.price || 0),
  )

  const isWhiteGloveShipping = productComparisionsBrands?.[0]?.additionalProperties?.find(
    item => item.name === WGD_PC_ADDITONAL_ATTRIBUTE_NAME,
  )

  const isMembershipFee = productComparisionsBrands?.[0]?.additionalProperties?.find(
    item => item.name === MF_PC_ADDITONAL_ATTRIBUTE_NAME,
  )

  const isPricePerServing = productComparisionsBrands?.[0]?.additionalProperties?.find(
    item => item.name === SERVING_PC_ADDITONAL_ATTRIBUTE_NAME,
  )
  const isServings = productComparisionsBrands?.[0]?.additionalProperties?.find(
    item => item.name === PPS_PC_ADDITONAL_ATTRIBUTE_NAME,
  )

  return (
    <div className={styles.productPriceComparision}>
      <div className={styles.productPriceComparision__titleWrapper}>
        <span className={styles.productPriceComparision__titleWrapper__title}>{t('beyond_compare')}</span>
        {tooltip && (
          <ToolTipWrapper
            topTipPosition={isMobile ? TOP_TIP_POSITION.BOTTOM : TOP_TIP_POSITION.TOP}
            className={styles.productPriceComparision__titleWrapper__infoIcon}
            toolTipContentRenderer={() => tooltip}
            toolTipContentClassName={styles.productPriceComparision__titleWrapper__toolTipContentWrapper}
          >
            <IButtonIconContainer />
          </ToolTipWrapper>
        )}
      </div>
      <table>
        <thead>
          <tr className={styles.productPriceComparision__headerRow}>
            <td></td>
            <th
              className={`${styles.productPriceComparision__headerRow__cell} ${styles.productPriceComparision__firstCell}`}
            >
              <div className={styles.productPriceComparision__headerRow__cell__textContainer}>{t('quince')}</div>
            </th>
            {productComparisionsBrands?.map(br => {
              const name = br.brand.split(' ')
              const regex = isMobile ? /.{1,8}/g : /.{1,12}/g
              const brandName = name.map(ele => ele.match(regex)?.join('-')).join(' ')
              return (
                <th className={styles.productPriceComparision__headerRow__cell} key={br.brand}>
                  <div className={styles.productPriceComparision__headerRow__cell__textContainer}>{brandName}</div>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          <tr className={styles.productPriceComparision__bodyRow}>
            <th className={styles.productPriceComparision__bodyRow__titleCell}>{t('price')}</th>
            <td
              className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__firstCell}`}
            >
              {formatPrice(quincePrice) || ''}
            </td>
            {productComparisionsBrands?.map(br => (
              <td className={styles.productPriceComparision__bodyRow__cell} key={br.brand}>
                {formatPrice(br.prices[0]?.price || '') || ''}
              </td>
            ))}
          </tr>

          <ComparisonRow
            isVisible={!!isWhiteGloveShipping}
            quinceField={'Free'}
            attributeName={WGD_PC_ADDITONAL_ATTRIBUTE_NAME}
            productComparisionsBrands={productComparisionsBrands ?? []}
          />
          <ComparisonRow
            isVisible={!!isMembershipFee}
            quinceField={'Free'}
            attributeName={MF_PC_ADDITONAL_ATTRIBUTE_NAME}
            productComparisionsBrands={productComparisionsBrands ?? []}
          />
          <ComparisonRow
            isVisible={!!isServings}
            quinceField={servings}
            attributeName={SERVING_PC_ADDITONAL_ATTRIBUTE_NAME}
            productComparisionsBrands={productComparisionsBrands ?? []}
          />
          <ComparisonRow
            isVisible={!!isPricePerServing}
            quinceField={pricePerServing ? formatPrice(pricePerServing) : ''}
            attributeName={PPS_PC_ADDITONAL_ATTRIBUTE_NAME}
            productComparisionsBrands={productComparisionsBrands ?? []}
          />

          {showGiftCardCredit ? (
            <>
              <tr className={styles.productPriceComparision__bodyRow}>
                <th className={styles.productPriceComparision__bodyRow__titleCell}>{t('gift_card_credit')}</th>
                <td
                  className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__firstCell}`}
                >
                  ${giftCardInformation?.giftCardCredit?.toFixed(2) || ''}
                </td>
                {giftCardInformation?.productComparisionsBrands?.map(br => (
                  <td className={styles.productPriceComparision__bodyRow__cell} key={br.brand}>
                    ${br.prices[0]?.giftCardCredit?.toFixed(2) || 0}
                  </td>
                ))}
              </tr>
              <tr className={styles.productPriceComparision__bodyRow}>
                <th className={styles.productPriceComparision__bodyRow__titleCell}>Savings (%)</th>
                <td
                  className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__firstCell}`}
                >
                  {giftCardInformation?.savings || ''}
                </td>
                {giftCardInformation?.productComparisionsBrands?.map(br => (
                  <td className={styles.productPriceComparision__bodyRow__cell} key={br.brand}>
                    <p>{br.prices[0]?.savings || '0%'}</p>
                  </td>
                ))}
              </tr>
            </>
          ) : (
            <tr className={styles.productPriceComparision__bodyRow}>
              <th className={styles.productPriceComparision__bodyRow__titleCell}>Savings (%)</th>
              <td
                className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__firstCell}`}
              >
                <YellowCircleContainer />
              </td>
              {isVitamin ? (
                <>
                  {productComparisionsBrands?.map(br => (
                    <td className={styles.productPriceComparision__bodyRow__cell} key={br.brand}>
                      <p>{getVitaminSavingsPercentage(br, pricePerServing)}%</p>
                    </td>
                  ))}
                </>
              ) : (
                <>
                  {productComparisionsBrands?.map(br => (
                    <td className={styles.productPriceComparision__bodyRow__cell} key={br.brand}>
                      <p>
                        {Math.round(
                          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                          ((br.prices[0]?.price +
                            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                            getBrandsExtraPrice(br, WGD_PC_ADDITONAL_ATTRIBUTE_NAME) +
                            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                            getBrandsExtraPrice(br, MF_PC_ADDITONAL_ATTRIBUTE_NAME) -
                            quincePrice) *
                            100) /
                            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                            (br.prices[0]?.price +
                              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                              getBrandsExtraPrice(br, WGD_PC_ADDITONAL_ATTRIBUTE_NAME) +
                              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                              getBrandsExtraPrice(br, MF_PC_ADDITONAL_ATTRIBUTE_NAME)),
                        )}
                        %
                      </p>
                    </td>
                  ))}
                </>
              )}
            </tr>
          )}

          {showDollarComparisonPrice && (
            <tr className={styles.productPriceComparision__bodyRow}>
              <th className={styles.productPriceComparision__bodyRow__titleCell}>Savings ($)</th>
              <td
                className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__firstCell}`}
              >
                <YellowCircleContainer />
              </td>
              {productComparisionsBrands?.map(br => (
                <td className={styles.productPriceComparision__bodyRow__cell} key={br.brand}>
                  <p>{formatPrice((br.prices[0]?.price ?? 0) - quincePrice) || ''}</p>
                </td>
              ))}
            </tr>
          )}

          {(
            productComparisionsBrands?.[0]?.additionalProperties
              ?.filter(
                item =>
                  item.name != MF_PC_ADDITONAL_ATTRIBUTE_NAME &&
                  item.name != WGD_PC_ADDITONAL_ATTRIBUTE_NAME &&
                  item.name != SERVING_PC_ADDITONAL_ATTRIBUTE_NAME &&
                  item.name != PPS_PC_ADDITONAL_ATTRIBUTE_NAME,
              )
              .map(ap => ap.name) || []
          ).map((apName: string) => {
            return (
              <tr className={styles.productPriceComparision__bodyRow} key={apName}>
                <th className={styles.productPriceComparision__bodyRow__titleCell}>{apName}</th>
                <td
                  className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__firstCell}`}
                >
                  <YellowCircleContainer />
                </td>
                {productComparisionsBrands?.map(br => (
                  <td className={styles.productPriceComparision__bodyRow__cell} key={br.brand}>
                    <AdditionalPropertyComponent additionalProperties={br.additionalProperties} apName={apName} />
                  </td>
                ))}
              </tr>
            )
          })}

          {!isWhiteGloveShipping && (
            <tr className={styles.productPriceComparision__bodyRow}>
              <th className={styles.productPriceComparision__bodyRow__titleCell}>{t('free_shipping')}</th>
              <td
                className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__firstCell}`}
              >
                <YellowCircleContainer />
              </td>
              {productComparisionsBrands?.map(br => (
                <td className={styles.productPriceComparision__bodyRow__cell} key={br.brand}>
                  {br.freeShipping ? <GrayCircleContainer /> : <CrossIconContainer />}
                </td>
              ))}
            </tr>
          )}
          {!hideFreeReturns && !isWhiteGloveShipping && (
            <tr className={styles.productPriceComparision__bodyRow}>
              <th className={styles.productPriceComparision__bodyRow__titleCell}>
                {hasRestockingFee ? 'Easy ' : 'Free '} Returns
              </th>
              <td
                className={`${styles.productPriceComparision__bodyRow__cell} ${styles.productPriceComparision__firstCell} ${styles.productPriceComparision__bodyRow__cell__yearLongReturns}`}
              >
                {t('365_days')}
              </td>
              {productComparisionsBrands?.map(br => (
                <td className={styles.productPriceComparision__bodyRow__cell} key={br.brand}>
                  {br.freeReturns === 0 ? 'Final Sale' : `${br.freeReturns} days`}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export default React.memo(ProductPriceComparision)
